import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  DatePicker,
  Button,
  Statistic,
  Space,
  Typography,
  Spin,
  theme,
} from "antd";
import {
  DownloadOutlined,
  DollarOutlined,
  WalletOutlined,
  BankOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import { authenticatedFetch } from "@/libs/api";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const fmtMoney = (val) =>
  `$${Number(val || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function Statement() {
  const { token } = theme.useToken();
  const [pageSize, setPageSize] = useState(31);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);
  const [totals, setTotals] = useState({ credit: 0, debit: 0, closingBalance: null });

  // Fetch the daily settlement ledger for the selected month. The backend
  // resolves "me" to the logged-in user's own company (and scope-checks),
  // so no separate profile lookup is needed.
  useEffect(() => {
    if (!selectedMonth) return;
    const fetchLedger = async () => {
      setLoading(true);
      try {
        const month = selectedMonth.format("YYYY-MM");
        const resp = await authenticatedFetch(
          `${API_BASE}/api/companies/me/daily-ledger?month=${month}`
        );
        const result = await resp.json();
        if (result.success && result.data) {
          setCompanyName(result.data.companyName || "");
          setDays(
            (result.data.days || []).map((d) => ({
              key: d.date,
              date: dayjs(d.date).format("YYYY-MM-DD"),
              credit: Number(d.credit) || 0,
              debit: Number(d.debit) || 0,
              closingBalance: Number(d.closing_balance) || 0,
            }))
          );
          setTotals(result.data.totals || { credit: 0, debit: 0, closingBalance: null });
        } else {
          setDays([]);
          setTotals({ credit: 0, debit: 0, closingBalance: null });
        }
      } catch (err) {
        console.error("Error fetching daily ledger:", err);
        setDays([]);
        setTotals({ credit: 0, debit: 0, closingBalance: null });
      } finally {
        setLoading(false);
      }
    };
    fetchLedger();
  }, [selectedMonth]);

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Allocated (Credit)",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      render: (val) => (
        <Text style={{ color: token.colorSuccess, fontWeight: 500 }}>{fmtMoney(val)}</Text>
      ),
    },
    {
      title: "Payments (Debit)",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      render: (val) =>
        val > 0 ? <Text style={{ color: token.colorError }}>{fmtMoney(val)}</Text> : "—",
    },
    {
      title: "Balance",
      dataIndex: "closingBalance",
      key: "closingBalance",
      align: "right",
      render: (val) => <Text strong>{fmtMoney(val)}</Text>,
    },
  ];

  const handleExport = () => {
    const header = "Date,Allocated (Credit),Payments (Debit),Balance";
    const lines = days.map(
      (d) => `${d.date},${d.credit.toFixed(2)},${d.debit.toFixed(2)},${d.closingBalance.toFixed(2)}`
    );
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `statement-${companyName || "company"}-${selectedMonth.format("YYYY-MM")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout active="statement">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Statement
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Daily settlement allocations and balance{companyName ? ` — ${companyName}` : ""}
        </Text>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Month
            </Text>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(date) => date && setSelectedMonth(date)}
              allowClear={false}
              size="large"
              style={{ width: 200 }}
            />
          </div>
          <div style={{ paddingTop: 28 }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              onClick={handleExport}
              disabled={days.length === 0}
            >
              Export CSV
            </Button>
          </div>
        </Space>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Allocated This Month"
              value={totals.credit}
              prefix={<DollarOutlined />}
              valueStyle={{ color: token.colorSuccess }}
              formatter={(value) => fmtMoney(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Payments This Month"
              value={totals.debit}
              prefix={<WalletOutlined />}
              valueStyle={{ color: token.colorError }}
              formatter={(value) => fmtMoney(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Balance (End of Month)"
              value={totals.closingBalance ?? 0}
              prefix={<BankOutlined />}
              valueStyle={{ color: token.colorPrimary }}
              formatter={(value) =>
                totals.closingBalance === null ? "—" : fmtMoney(value)
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Data Table */}
      <Card
        title="Daily Settlement"
        bordered={false}
        style={{ borderRadius: 12 }}
        styles={{
          header: { borderBottom: `1px solid ${token.colorBorderSecondary}` },
        }}
      >
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={days}
            pagination={makePagination(pageSize, setPageSize)}
            scroll={{ y: 4 * 55 }}
          />
        </Spin>
      </Card>
    </PageLayout>
  );
}
